import { useField } from "formik";
import { Checkbox, Flex, Text, IconButton, Button } from "@radix-ui/themes";
import { useState, useEffect } from "react";
import { Check, X, Pencil, CheckSquare, Square } from "lucide-react";
import { cn } from "@/utils/cn";

interface CheckboxFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  className?: string;
  disabled?: boolean;
  description?: string;
  isEditing?: boolean; // Parent component edit mode
  individualEditAllowed?: boolean; // Whether individual field editing is allowed
  marginBottom?: boolean;
  onChange?: (value: boolean) => void;
  onEditSaved?: (name: string, value: boolean) => void;
  onIndividualEditModeChanged?: (isIndividualEditing: boolean) => void;
}

export default function CheckboxField({
  name,
  label,
  required = false,
  disabled,
  description,
  isEditing = true,
  individualEditAllowed = false,
  marginBottom = true,
  onChange,
  onEditSaved,
  onIndividualEditModeChanged,
}: CheckboxFieldProps) {
  const [field, { error, touched }, helpers] = useField(name);
  const [isIndividualEditing, setIsIndividualEditing] = useState(false);
  const [tempValue, setTempValue] = useState<boolean>(false);

  useEffect(() => {
    if (field.value !== undefined) {
      setTempValue(!!field.value);
    }
  }, [field.value]);

  // Reset individual editing state when parent edit mode changes
  useEffect(() => {
    if (isEditing) {
      setIsIndividualEditing(false);
    }
  }, [isEditing]);

  const handleEdit = () => {
    const newValue = !field.value;
    setIsIndividualEditing(true);
    setTempValue(newValue);
    onIndividualEditModeChanged?.(true);
  };

  const handleSave = () => {
    helpers.setValue(tempValue);
    onChange?.(tempValue);
    onEditSaved?.(name, tempValue);
    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
  };

  const handleCancel = () => {
    setTempValue(!!field.value);
    setIsIndividualEditing(false);
    onIndividualEditModeChanged?.(false);
  };

  const handleChange = (checked: boolean) => {
    if (isIndividualEditing) {
      setTempValue(checked);
    } else {
      field.onChange({
        target: {
          name: name,
          value: checked,
        },
      });
      onChange?.(checked);
    }
  };

  // Determine current field state
  const shouldRenderTextOnly = !isEditing && !isIndividualEditing;
  const shouldShowEditIcon =
    !isEditing && individualEditAllowed && !isIndividualEditing;
  const currentValue = isIndividualEditing ? tempValue : !!field.value;

  return (
    <div className={cn(marginBottom && "mb-4")}>
      {shouldRenderTextOnly ? (
        <Flex align="center" justify="between">
          <Flex 
            align="center" 
            gap="2"
            onClick={individualEditAllowed ? handleEdit : undefined}
            style={{ 
              cursor: individualEditAllowed ? 'pointer' : undefined,
              padding: "4px",
              borderRadius: "var(--radius-2)",
              flex: 1,
              marginLeft: "-4px"
            }}
            onMouseEnter={(e) => {
              if (individualEditAllowed) {
                e.currentTarget.style.backgroundColor = "var(--gray-a3)";
              }
            }}
            onMouseLeave={(e) => {
              if (individualEditAllowed) {
                e.currentTarget.style.backgroundColor = "transparent";
              }
            }}
          >
            {currentValue ? (
              <CheckSquare className="w-4 h-4 text-accent-9" />
            ) : (
              <Square className="w-4 h-4 text-gray-9" />
            )}
            <Text size="2" weight="medium">
              {label}
            </Text>
          </Flex>
          {shouldShowEditIcon && (
            <IconButton
              size="1"
              variant="ghost"
              onClick={handleEdit}
              aria-label="Edit"
            >
              <Pencil className="w-4 h-4" />
            </IconButton>
          )}
        </Flex>
      ) : (
        <div>
          <Text as="label" size="2">
            <Flex gap="3" align="center">
              <Checkbox
                id={name}
                disabled={disabled}
                checked={currentValue}
                onCheckedChange={handleChange}
                required={required}
                size="2"
              />
              <Text weight="medium" style={{ userSelect: "none" }}>
                {label}
                {required && (
                  <Text color="red" style={{ marginLeft: "2px" }}>
                    *
                  </Text>
                )}
              </Text>
            </Flex>
          </Text>

          {isIndividualEditing && !isEditing && (
            <Flex gap="2" mt="2" justify="start">
              <Button
                size="1"
                variant="soft"
                color="red"
                onClick={handleCancel}
              >
                <X width={12} height={12} style={{ marginRight: 4 }} />
                Cancel
              </Button>
              <Button
                size="1"
                variant="soft"
                color="green"
                onClick={handleSave}
              >
                <Check width={12} height={12} style={{ marginRight: 4 }} />
                Save
              </Button>
            </Flex>
          )}
        </div>
      )}

      {description && (
        <Text
          className="mt-2 text-sm"
          color="gray"
          style={{ paddingLeft: "4px" }}
        >
          {description}
        </Text>
      )}

      {error && touched && (
        <Text
          className="mt-1 text-sm"
          color="red"
          style={{ paddingLeft: "4px" }}
        >
          {error}
        </Text>
      )}
    </div>
  );
}
